/**
 * Provider payables — `/v1/admin/payables*`, `/v1/admin/release-approvals*`.
 *
 * **Every row carries the server's release decision, and the screen never re-derives it.** The
 * six conditions live in `can_release` on the backend; a console that assembled its own opinion
 * from status and dates would agree with the worker today and disagree after the next edit — and
 * the disagreement would be a pandit either paid early or not paid at all.
 *
 * **"Released" and "asked" are different outcomes.** `useRequestRelease` returns which one
 * happened rather than a bare 200, because above the auto-release ceiling nothing moved and the
 * operator has to know a colleague now has to look.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedClient } from '../../auth/session';
import { keys } from '../queryClient';
import type { components } from '../../../api/generated/schema';

export type PayableRow = components['schemas']['PayableRow'];
export type PayableBoardRow = components['schemas']['PayableBoardRow'];
export type PayableDetail = components['schemas']['PayableDetail'];
export type PayableDecision = components['schemas']['PayableDecision'];
export type ApprovalRow = components['schemas']['ApprovalRow'];
type PayableList = components['schemas']['PayableListResponse'];
type ReleaseResponse = components['schemas']['ReleaseRequestResponse'];

export interface PayableQuery extends Record<string, unknown> {
  page: number;
  page_size: number;
  payable_status?: string;
  pandit_profile_id?: number;
}

/** Exported for its own test: an absent filter and an explicit one are different requests. */
export function payableSearch(params: PayableQuery): string {
  const search = new URLSearchParams({
    page: String(params.page),
    page_size: String(params.page_size),
  });
  if (params.payable_status !== undefined) {
    search.set('payable_status', params.payable_status);
  }
  if (params.pandit_profile_id !== undefined) {
    search.set('pandit_profile_id', String(params.pandit_profile_id));
  }
  return search.toString();
}

export function usePayables(params: PayableQuery) {
  return useQuery({
    queryKey: keys.payables.board(params),
    queryFn: () =>
      authedClient.request<PayableList>(`/v1/admin/payables?${payableSearch(params)}`),
  });
}

export function usePayable(payableId: number | null) {
  return useQuery({
    queryKey: keys.payables.one(payableId ?? 0),
    queryFn: () => authedClient.request<PayableDetail>(`/v1/admin/payables/${payableId}`),
    enabled: payableId !== null,
  });
}

/**
 * Coarse invalidation on purpose. Releasing one payable changes the board's counts, that
 * payable's detail and — above the ceiling — an approval queue; a hand-maintained map of which
 * keys each action touches goes stale the first time somebody adds a filter.
 */
function usePayableMutation<TArgs, TData>(fn: (args: TArgs) => Promise<TData>) {
  const client = useQueryClient();
  return useMutation<TData, unknown, TArgs>({
    mutationFn: fn,
    onSuccess: () => client.invalidateQueries({ queryKey: keys.payables.all() }),
  });
}

export function useHoldPayable() {
  return usePayableMutation(
    ({ payableId, kind, reason }: { payableId: number; kind: string; reason: string }) =>
      authedClient.request(`/v1/admin/payables/${payableId}/hold`, {
        method: 'POST',
        body: { kind, reason },
      }),
  );
}

/** The four endings are not interchangeable — `window_restarted` gives the devotee time back. */
export function useResumePayable() {
  return usePayableMutation(({ payableId, action }: { payableId: number; action: string }) =>
    authedClient.request<PayableDetail>(`/v1/admin/payables/${payableId}/resume`, {
      method: 'POST',
      body: { action },
    }),
  );
}

export function useRequestRelease() {
  return usePayableMutation(({ payableId, reason }: { payableId: number; reason: string }) =>
    authedClient.request<ReleaseResponse>(`/v1/admin/payables/${payableId}/release-request`, {
      method: 'POST',
      body: { reason },
    }),
  );
}

/** A second person decides. The server refuses the requester, whatever this screen offers. */
export function useDecideApproval() {
  return usePayableMutation(
    ({ approvalId, decision }: { approvalId: number; decision: 'approve' | 'reject' }) =>
      authedClient.request<ApprovalRow>(
        `/v1/admin/release-approvals/${approvalId}/${decision}`,
        { method: 'POST' },
      ),
  );
}
